import type { UserVariables } from "components/shared/AgreementModal";

import { isTitleScopedAgreementVariableKey } from "./agreementVariableKeys";

export type AgreementWorkTitle = {
    id: number;
    name: string;
    ean13: string;
    avatarUrl: string | null;
    variables: Record<string, string>;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const formatTitleCustomFieldValue = (value: string, valueType?: string) => {
    if (valueType === "date" && value) {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
            return date.toLocaleDateString("fr-FR");
        }
    }
    return value;
};

export const toAgreementWorkTitle = (
    value: unknown,
    { workName }: { workName?: string } = {},
): AgreementWorkTitle | null => {
    const title = asRecord(value);
    if (!title) return null;
    const id = Number(title.id);
    if (!Number.isFinite(id)) return null;

    const nestedVariables = asRecord(title.variables);
    const ean13 = String(nestedVariables?.ean13 ?? title.ean13 ?? "");
    const variables: Record<string, string> = { ean13 };
    if (workName) {
        variables.title = workName;
    }
    if (nestedVariables) {
        for (const [key, nestedValue] of Object.entries(nestedVariables)) {
            if (typeof nestedValue === "string" || typeof nestedValue === "number") {
                variables[key] = String(nestedValue);
            }
        }
    }
    if (Array.isArray(title.customFields)) {
        for (const field of title.customFields) {
            const customField = asRecord(field);
            if (typeof customField?.uuid !== "string") continue;
            variables[`meta:${customField.uuid}`] = formatTitleCustomFieldValue(
                String(customField.value ?? ""),
                typeof customField.valueType === "string" ? customField.valueType : undefined,
            );
        }
    }

    return {
        id,
        name: String(title.name ?? ""),
        ean13,
        avatarUrl: typeof title.avatarUrl === "string" ? title.avatarUrl : null,
        variables,
    };
};

export const getAgreementWorkTitles = (variables: unknown): AgreementWorkTitle[] => {
    if (!variables || typeof variables !== "object") return [];
    const workTitles = (variables as { workTitles?: unknown }).workTitles;
    if (!Array.isArray(workTitles)) return [];
    return workTitles
        .map((title) => toAgreementWorkTitle(title))
        .filter((title): title is AgreementWorkTitle => title != null);
};

export const getSelectedAgreementTitleId = (variables: unknown, workTitles: AgreementWorkTitle[]) => {
    if (!variables || typeof variables !== "object") return workTitles[0]?.id;
    const selectedTitleId = Number((variables as { selectedTitleId?: unknown }).selectedTitleId);
    return workTitles.find((title) => title.id === selectedTitleId)?.id ?? workTitles[0]?.id;
};

export const resolveAgreementWorkTitles = (
    userVariables: unknown,
    agreementVariables: unknown,
    fetchedTitles: unknown[],
    workName: string,
): AgreementWorkTitle[] => {
    const stored = getAgreementWorkTitles(userVariables);
    const fromAgreement = stored.length ? stored : getAgreementWorkTitles(agreementVariables);
    const storedVariablesByTitleId = new Map(fromAgreement.map((title) => [title.id, title.variables]));

    const fetched = fetchedTitles
        .map((title) => toAgreementWorkTitle(title, { workName }))
        .filter((title): title is AgreementWorkTitle => title != null)
        .map((title) => ({
            ...title,
            variables: {
                ...(storedVariablesByTitleId.get(title.id) ?? {}),
                ...title.variables,
            },
        }));

    return fetched.length ? fetched : fromAgreement;
};

export const applySelectedTitleVariables = (
    current: UserVariables,
    selectedTitle: AgreementWorkTitle,
    workTitles: AgreementWorkTitle[],
    workName: string,
    titleCustomFieldUuids: Set<string>,
    usedVariableKeys?: string[],
): UserVariables => {
    const usedKeys = usedVariableKeys?.length ? new Set(usedVariableKeys) : null;
    const nextVariables: UserVariables = { ...current };

    for (const key of Object.keys(nextVariables)) {
        if (!isTitleScopedAgreementVariableKey(key, titleCustomFieldUuids)) continue;
        if (usedKeys && !usedKeys.has(key)) {
            delete nextVariables[key];
            continue;
        }
        if (key === "title") {
            nextVariables[key] = workName || selectedTitle.variables.title || selectedTitle.name;
            continue;
        }
        nextVariables[key] = selectedTitle.variables[key] ?? "";
    }

    return Object.assign(nextVariables, {
        selectedTitleId: selectedTitle.id,
        workTitles,
    }) as UserVariables;
};
